import { UPLOAD_PURPOSE_PREFLIGHT_ASSET } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_UPLOAD_MS } from "@/lib/query-constants";

export interface UploadPresignResult {
  uploadId: string;
  bucket: string;
  objectKey: string;
  uploadUrl: string;
  expiresAt: string;
  requiredHeaders: Record<string, string>;
  requiresMemoryStage: boolean;
  /** Dev/test + GCS: send bytes to API instead of browser PUT (skips bucket CORS). */
  requiresServerStage: boolean;
}

export interface UploadConfirmResult {
  uploadId: string;
  status: string;
  contentType: string | null;
  sizeBytes: number | null;
  checksumMd5: string | null;
}

export async function presignPreflightUpload(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_PREFLIGHT_ASSET,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

/** Dev/test only — stages bytes when the presigned URL is a memory-upload:// stub. */
export async function stageMemoryPreflightUpload(input: {
  tenantId: string;
  uploadId: string;
  contentType: string;
  file: File;
  signal?: AbortSignal;
}): Promise<{ staged: boolean; sizeBytes: number }> {
  const form = new FormData();
  form.append("tenantId", input.tenantId);
  form.append("uploadId", input.uploadId);
  form.append("contentType", input.contentType);
  form.append("file", input.file, input.file.name);

  const { data } = await authFetch<{ staged: boolean; sizeBytes: number }>(
    "/uploads/stage-memory",
    {
      method: "POST",
      body: form,
      signal: input.signal,
      timeoutMs: API_REQUEST_TIMEOUT_UPLOAD_MS,
    },
  );
  return data;
}

export async function putToSignedUrl(
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: requiredHeaders,
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Storage upload failed (${res.status})`);
  }
}

export interface PutProgressOptions {
  /** Receives 0–100 upload percentage when the browser reports it. */
  onProgress?: (percent: number) => void;
  /** Abort the in-flight PUT (frees a concurrency slot). */
  signal?: AbortSignal;
}

/**
 * Progress-capable variant of {@link putToSignedUrl} backed by `XMLHttpRequest`
 * (the `fetch` PUT cannot report upload progress). Falls back to indeterminate
 * when `xhr.upload` is unavailable. Reject reasons: `AbortError` on cancel,
 * otherwise a storage failure message.
 */
export function putToSignedUrlWithProgress(
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
  options: PutProgressOptions = {},
): Promise<void> {
  const { onProgress, signal } = options;
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    for (const [key, value] of Object.entries(requiredHeaders)) {
      xhr.setRequestHeader(key, value);
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort);
    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    xhr.timeout = API_REQUEST_TIMEOUT_UPLOAD_MS;
    xhr.ontimeout = () => {
      cleanup();
      reject(new Error("Storage upload timed out"));
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Storage upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("Storage upload failed (network error)"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    xhr.send(file);
  });
}

export async function confirmPreflightUpload(input: {
  tenantId: string;
  uploadId: string;
}): Promise<UploadConfirmResult> {
  const { data } = await authFetch<UploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function runPreflightUploadFlow(input: {
  tenantId: string;
  file: File;
  onStep?: (step: string) => void;
}): Promise<UploadConfirmResult> {
  const presign = await presignPreflightUpload({
    tenantId: input.tenantId,
    contentType: input.file.type,
    sizeBytes: input.file.size,
    originalName: input.file.name,
  });
  input.onStep?.("presigned");

  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType: input.file.type,
      file: input.file,
    });
    input.onStep?.(
      presign.requiresMemoryStage ? "staged (memory backend)" : "staged (API → GCS)",
    );
  } else {
    await putToSignedUrl(presign.uploadUrl, input.file, presign.requiredHeaders);
    input.onStep?.("uploaded to storage");
  }

  const confirmed = await confirmPreflightUpload({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
  });
  input.onStep?.("confirmed");
  return confirmed;
}
